import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { StickyNote, MessageSquareText } from "lucide-react";
import Notes from "@/pages/Notes";
import CastNotes from "@/pages/CastNotes";

const TABS_ADMIN = [
  { id: "cast", label: "Cast notes", icon: MessageSquareText, hint: "Rehearsal notes sent to specific cast members." },
  { id: "production", label: "Production notes", icon: StickyNote, hint: "Staff notes — tag cast to share individual items." },
];

// Cast users only get a single tab: "My notes" backed by tagged production notes.
const TABS_CAST = [
  { id: "cast", label: "My cast notes", icon: MessageSquareText, hint: "Notes addressed to you by staff." },
  { id: "production", label: "Tagged notes", icon: StickyNote, hint: "Production notes where you were tagged." },
];

/**
 * Notes hub — collapses Cast Notes + Production Notes into one sidebar
 * anchor. Cast see the same tabs but scoped by backend visibility.
 * Query param `?tab=cast|production` drives the active tab, and `?new=1`
 * lets the "+ New" menu land users straight in the compose flow (child
 * components can pick that up in a follow-up iteration).
 */
export default function NotesHub() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initial = params.get("tab") || "production";
  const tabs = user.role === "cast" ? TABS_CAST : TABS_ADMIN;
  const validInitial = tabs.find((t) => t.id === initial)?.id || tabs[0].id;
  const [tab, setTab] = useState(validInitial);

  const setActive = (id) => {
    setTab(id);
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  };

  const active = useMemo(() => tabs.find((t) => t.id === tab) || tabs[0], [tabs, tab]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="notes-hub">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Show</div>
        <h1 className="mt-1 font-display text-4xl tracking-tight font-light">{user.role === "cast" ? "My Notes" : "Notes"}</h1>
        <p className="mt-1 text-zinc-400 text-sm">{active.hint}</p>
      </header>

      <div className="flex flex-wrap gap-1 border border-white/10 rounded-md bg-[#121212] p-1 w-fit" data-testid="notes-tabs">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`notes-tab-${id}`}
            onClick={() => setActive(id)}
            className={`h-9 px-3 rounded text-sm inline-flex items-center gap-1.5 transition-colors
              ${tab === id ? "bg-gold text-black font-semibold" : "text-zinc-300 hover:bg-white/5"}`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="pt-2">
        {tab === "cast" && <CastNotes embedded />}
        {tab === "production" && <Notes embedded />}
      </div>
    </div>
  );
}
